<script setup lang="ts">
import type {Article} from '~/types/legacy'
import type {Populated} from '~/types/populated'

defineProps<{
  article: Populated<Article>
}>()

const {formatDate} = useDate()

const localePath = useLocalePath()
</script>

<template>
  <NuxtLink
    :to="localePath(`/blog/${article.slug}`)"
    class="group flex flex-col gap-4 rounded-lg p-2 transition hover:bg-black/5 hover:bordershadow-lg-gray md:flex-row md:items-end md:gap-4 lg:gap-6"
  >
    <div class="h-full w-full">
      <NuxtImg
        v-if="article.image"
        v-bind="resolveMediaProps(article.image)"
        :width="640"
        :height="400"
        loading="lazy"
        densities="x1 x2"
        fit="cover"
        class="h-full w-full rounded object-cover transition bordershadow-gray"
      />
    </div>

    <div
      class="group absolute bottom-4 left-4 right-2 flex w-[calc(100%-2rem)] flex-col rounded bg-black/50 p-4 text-white backdrop-blur-sm bordershadow-gray md:px-8 md:py-6"
    >
      <Paragraph
        size="small"
        class="mb-0 hidden max-h-0 overflow-hidden text-white opacity-0 transition-all duration-300 group-hover:mb-4 group-hover:max-h-6 group-hover:opacity-100 sm:flex"
      >
        <time class="flex items-center gap-1">
          <div v-if="article.category" class="flex items-center gap-1">
            <span class="font-medium text-main-50">{{ article.category.name }} </span>
            <span>—</span>
          </div>
          {{ article.createdAt ? formatDate(new Date(article?.createdAt)) : '' }}
        </time>
      </Paragraph>

      <Heading as="h2" size="medium" class="mb-0 text-white transition-all group-hover:md:mb-2">
        {{ article.title }}
      </Heading>

      <Paragraph
        size="normal"
        class="line-clamp-3 hidden max-h-0 overflow-hidden text-white transition-all duration-300 group-hover:max-h-32 sm:flex"
      >
        {{ article.description }}
      </Paragraph>

      <span
        class="group mt-0 hidden max-h-0 items-center gap-x-2 overflow-hidden font-medium text-white transition-all duration-300 group-hover:mt-4 group-hover:max-h-4 sm:flex"
      >
        {{ $t('blog.read_more') }}
        <Icon name="lucide:move-right" />
      </span>
    </div>
  </NuxtLink>
</template>
